import { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { api } from '../utils/api';
import { formatCurrency, formatDateShort } from '../utils/format';
import { Plus, Check, X, Trash2, AlertCircle, Wallet } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const KATEGORILER = [
  'Kira',
  'Fatura',
  'Maaş',
  'Vergi',
  'Sigorta',
  'Bakım/Onarım',
  'Malzeme',
  'Diğer'
];

export default function DigerOdemeler() {
  const { currentUser } = useApp();
  const [odemeler, setOdemeler] = useState([]);
  const [vadesiYaklasanlar, setVadesiYaklasanlar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [filter, setFilter] = useState('hepsi');
  const [addForm, setAddForm] = useState({
    aciklama: '',
    tutar: '',
    vadeTarihi: '',
    kategori: 'Diğer',
    not_: ''
  });

  useEffect(() => {
    loadOdemeler();
  }, [filter]);

  const loadOdemeler = async () => {
    try {
      const params = {
        subeId: currentUser.rol === 'sube_sorumlusu' ? currentUser.subeId : undefined,
        kullaniciId: currentUser.rol === 'eleman' ? currentUser.id : undefined,
        rol: currentUser.rol,
        durum: filter === 'hepsi' ? undefined : filter
      };

      const [odemelerRes, vadesiYaklasanRes] = await Promise.all([
        api.getDigerOdemeler(params),
        api.getVadesiYaklasanDigerOdemeler(30)
      ]);

      setOdemeler(odemelerRes.data);
      setVadesiYaklasanlar(vadesiYaklasanRes.data);
    } catch (error) {
      console.error('Error loading odemeler:', error);
      toast.error('Ödemeler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!addForm.aciklama || !addForm.tutar || !addForm.vadeTarihi) {
      toast.error('Lütfen tüm zorunlu alanları doldurun');
      return;
    }

    try {
      await api.createDigerOdeme({
        aciklama: addForm.aciklama,
        tutar: parseFloat(addForm.tutar),
        vadeTarihi: addForm.vadeTarihi,
        kategori: addForm.kategori,
        subeId: currentUser.subeId,
        girenKullaniciId: currentUser.id,
        not_: addForm.not_
      });
      
      toast.success('Ödeme eklendi');
      setShowAddDialog(false);
      setAddForm({
        aciklama: '',
        tutar: '',
        vadeTarihi: '',
        kategori: 'Diğer',
        not_: ''
      });
      loadOdemeler();
    } catch (error) {
      console.error('Error adding odeme:', error);
      toast.error('Ödeme eklenirken hata oluştu');
    }
  };

  const handleOde = async (odemeId) => {
    try {
      await api.odeDigerOdeme(odemeId);
      toast.success('Ödeme yapıldı olarak işaretlendi');
      loadOdemeler();
    } catch (error) {
      console.error('Error ode:', error);
      toast.error('İşlem başarısız');
    }
  };

  const handleGeriAl = async (odemeId) => {
    try {
      await api.geriAlDigerOdeme(odemeId);
      toast.success('Ödeme beklemede olarak işaretlendi');
      loadOdemeler();
    } catch (error) {
      console.error('Error geri al:', error);
      toast.error('İşlem başarısız');
    }
  };

  const handleDelete = async (odemeId) => {
    if (!window.confirm('Bu ödemeyi silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      await api.deleteDigerOdeme(odemeId);
      toast.success('Ödeme silindi');
      loadOdemeler();
    } catch (error) {
      console.error('Error deleting odeme:', error);
      toast.error('Ödeme silinirken hata oluştu');
    }
  };

  const getDurumBadge = (durum) => {
    const styles = {
      beklemede: 'bg-amber-100 text-amber-800 border border-amber-300',
      odendi: 'bg-success/10 text-success border border-success'
    };

    const labels = {
      beklemede: 'Beklemede',
      odendi: 'Ödendi'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium ${styles[durum]}`}>
        {labels[durum]}
      </span>
    );
  };

  const getKategoriBadge = (kategori) => {
    const colors = {
      'Kira': 'bg-blue-100 text-blue-800',
      'Fatura': 'bg-purple-100 text-purple-800',
      'Maaş': 'bg-green-100 text-green-800',
      'Vergi': 'bg-red-100 text-red-800',
      'Sigorta': 'bg-teal-100 text-teal-800',
      'Bakım/Onarım': 'bg-orange-100 text-orange-800',
      'Malzeme': 'bg-indigo-100 text-indigo-800',
      'Diğer': 'bg-gray-100 text-gray-800'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium ${colors[kategori] || colors['Diğer']}`}>
        {kategori}
      </span>
    );
  };

  const toplamBekleyen = odemeler
    .filter(o => o.durum === 'beklemede')
    .reduce((sum, o) => sum + o.tutar, 0);

  const toplamOdenen = odemeler
    .filter(o => o.durum === 'odendi')
    .reduce((sum, o) => sum + o.tutar, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-textSecondary">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-8" data-testid="diger-odemeler-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
        <div>
          <h1 className="text-xl md:text-3xl font-heading font-bold text-textPrimary">Diğer Ödemeler</h1>
          <p className="text-xs md:text-base text-textSecondary mt-1 md:mt-2">Kira, fatura ve diğer ödemeleri takip edin</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-odeme-btn"
              className="bg-primary hover:bg-primaryHover text-primaryForeground"
            >
              <Plus className="h-5 w-5 mr-2" />
              Yeni Ödeme Ekle
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]" data-testid="add-odeme-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Yeni Ödeme Ekle</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">Açıklama *</label>
                <Input
                  data-testid="aciklama-input"
                  value={addForm.aciklama}
                  onChange={(e) => setAddForm({ ...addForm, aciklama: e.target.value })}
                  placeholder="Ödeme açıklaması"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">Kategori</label>
                <Select 
                  value={addForm.kategori} 
                  onValueChange={(value) => setAddForm({ ...addForm, kategori: value })}
                >
                  <SelectTrigger data-testid="kategori-select" className="w-full h-10">
                    <SelectValue placeholder="Kategori seçin" />
                  </SelectTrigger>
                  <SelectContent className="z-[100] bg-surface">
                    {KATEGORILER.map((kat) => (
                      <SelectItem key={kat} value={kat} className="cursor-pointer">{kat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">Tutar (₺) *</label>
                <Input
                  type="number"
                  step="0.01"
                  data-testid="tutar-input"
                  value={addForm.tutar}
                  onChange={(e) => setAddForm({ ...addForm, tutar: e.target.value })}
                  placeholder="0.00"
                  className="font-mono"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">Vade Tarihi *</label>
                <Input
                  type="date"
                  data-testid="vade-tarihi-input"
                  value={addForm.vadeTarihi}
                  onChange={(e) => setAddForm({ ...addForm, vadeTarihi: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">Not (Opsiyonel)</label>
                <Textarea
                  data-testid="not-input"
                  value={addForm.not_}
                  onChange={(e) => setAddForm({ ...addForm, not_: e.target.value })}
                  placeholder="Ek notlar..."
                  className="min-h-[80px]"
                />
              </div>
              <Button
                onClick={handleAdd}
                data-testid="submit-odeme-btn"
                className="w-full bg-primary hover:bg-primaryHover text-primaryForeground"
              >
                Kaydet
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Özet Kartları */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
        <div className="bg-surface border border-border p-4 md:p-6">
          <p className="text-xs md:text-sm text-textSecondary mb-1">Bekleyen Ödemeler</p>
          <p className="text-lg md:text-2xl font-mono font-bold text-amber-600">{formatCurrency(toplamBekleyen)}</p>
          <p className="text-xs text-textSecondary mt-1">
            {odemeler.filter(o => o.durum === 'beklemede').length} adet
          </p>
        </div>
        <div className="bg-surface border border-border p-4 md:p-6">
          <p className="text-xs md:text-sm text-textSecondary mb-1">Ödenen</p>
          <p className="text-lg md:text-2xl font-mono font-bold text-success">{formatCurrency(toplamOdenen)}</p>
          <p className="text-xs text-textSecondary mt-1">
            {odemeler.filter(o => o.durum === 'odendi').length} adet
          </p>
        </div>
        <div className="bg-surface border border-border p-4 md:p-6">
          <p className="text-xs md:text-sm text-textSecondary mb-1">Toplam</p>
          <p className="text-lg md:text-2xl font-mono font-bold text-textPrimary">{odemeler.length}</p>
          <p className="text-xs text-textSecondary mt-1">
            {formatCurrency(odemeler.reduce((sum, o) => sum + o.tutar, 0))}
          </p>
        </div>
      </div>

      {/* Vadesi Yaklaşan Uyarı */}
      {vadesiYaklasanlar.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-amber-900">
                30 Gün İçinde Vadesi Dolan {vadesiYaklasanlar.length} Ödeme
              </h3>
              <p className="text-sm text-amber-700 mt-1">
                Toplam: {formatCurrency(vadesiYaklasanlar.reduce((sum, o) => sum + o.tutar, 0))}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Filtreler */}
      <div className="flex gap-2 flex-wrap">
        {['hepsi', 'beklemede', 'odendi'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`filter-${f}`}
            className={`px-3 md:px-4 py-1.5 md:py-2 text-xs md:text-sm font-medium transition-colors ${
              filter === f
                ? 'bg-primary text-primaryForeground'
                : 'bg-surfaceSubtle text-textSecondary hover:bg-border'
            }`}
          >
            {f === 'hepsi' ? 'Hepsi' : f === 'beklemede' ? 'Beklemede' : 'Ödendi'}
          </button>
        ))}
      </div>

      {/* Ödeme Listesi */}
      {odemeler.length === 0 ? (
        <div className="bg-surface border border-border p-12 text-center">
          <Wallet className="h-16 w-16 text-textSecondary mx-auto mb-4" strokeWidth={1.5} />
          <p className="text-textSecondary">Henüz ödeme kaydı yok</p>
        </div>
      ) : (
        <div className="bg-surface border border-border">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="odemeler-table">
              <thead className="bg-surfaceSubtle border-b border-border">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Açıklama</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Kategori</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-textSecondary uppercase tracking-wider">Tutar</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Vade Tarihi</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Durum</th>
                  {currentUser?.rol !== 'eleman' && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">İşlem</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {odemeler.map((odeme, index) => (
                  <tr key={odeme.id} data-testid={`odeme-row-${index}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-textPrimary">{odeme.aciklama}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{getKategoriBadge(odeme.kategori)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-right font-bold text-textPrimary">
                      {formatCurrency(odeme.tutar)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-textPrimary">{formatDateShort(odeme.vadeTarihi)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{getDurumBadge(odeme.durum)}</td>
                    {currentUser?.rol !== 'eleman' && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex gap-2">
                          {odeme.durum === 'beklemede' && (
                            <button
                              onClick={() => handleOde(odeme.id)}
                              data-testid={`odendi-btn-${index}`}
                              className="text-success hover:text-success/80"
                              title="Ödendi"
                            >
                              <Check className="h-5 w-5" />
                            </button>
                          )}
                          {odeme.durum === 'odendi' && currentUser?.rol === 'admin' && (
                            <button
                              onClick={() => handleGeriAl(odeme.id)}
                              data-testid={`geri-al-btn-${index}`}
                              className="text-amber-600 hover:text-amber-700"
                              title="Beklemede'ye döndür"
                            >
                              <X className="h-5 w-5" />
                            </button>
                          )}
                          {currentUser?.rol === 'admin' && (
                            <button
                              onClick={() => handleDelete(odeme.id)}
                              data-testid={`delete-btn-${index}`}
                              className="text-error hover:text-error/80"
                              title="Sil"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
